'use client';

import { countryScopeLabelKey } from '@/lib/countries';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { flagUrlForCountry } from '@/lib/country-flags';
import { cn } from '@/lib/utils';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  FilterLabel,
  FIELD_LABEL_CLASS,
} from '@/new_components/searchresults/FilterControls';

/**
 * The country scope's flag. Rendered from the SVG set in
 * `public/media/flags/` — the same assets the header language switcher uses —
 * rather than a regional-indicator emoji: Windows ships no emoji flag glyphs
 * at all, so 🇩🇪 renders there as the letters "DE". Global has no country, so
 * it gets a globe icon.
 */
export function CountryFlag({
  value,
  className,
}: {
  value: string;
  className?: string;
}) {
  const url = flagUrlForCountry(value || null);
  if (!url) {
    return (
      <Globe className={cn('h-3.5 w-3.5 text-muted-foreground', className)} />
    );
  }
  return (
    <img
      src={url}
      alt=""
      aria-hidden
      className={cn('w-5 h-3.5 rounded-[2px] object-cover', className)}
    />
  );
}

export function CountryScopeControl({
  value,
  options,
  onChange,
  label,
}: {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  label?: string;
}) {
  const { t } = useTranslation();
  const heading = label ?? t('search.sidebar.countryScope');
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={null} className={FIELD_LABEL_CLASS}>
        {heading}
      </FilterLabel>
      <ToggleGroup
        type="single"
        variant="outline"
        value={value || 'all'}
        aria-label={heading}
        className="w-full"
        onValueChange={(next) => {
          if (next) onChange(next === 'all' ? '' : next);
        }}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option || 'all'}
            value={option || 'all'}
            className="h-11 lg:h-9 min-w-0 flex-1 gap-1.5 px-1 text-xs font-medium data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
          >
            <CountryFlag value={option} className="shrink-0" />
            {t(
              countryScopeLabelKey(option),
            )}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}
